import { Button, Form, Input, InputNumber, Space, Table } from "@arco-design/web-react";
import { copy } from "../utils/copy";
import { IconCopy } from "@arco-design/web-react/icon";
import { useEffect, useState } from "react";
import { check } from "../utils/check";
import { toHValue } from "../utils/wrap";
import { usePasswordCheck } from "../hooks/usePasswordCheck";

const FormItem = Form.Item;
const LinearSpeedCalculation = ({ onPasswordInvalid }) => {
  const { checkPasswordBeforeAction } = usePasswordCheck(onPasswordInvalid);
  const [btnState, setBtnState] = useState({ calc: false, reset: false });
  const [segReady, setSegReady] = useState(false);
  const [segRows, setSegRows] = useState([]);
  const [segDiameters, setSegDiameters] = useState([]);
  const [activeSegButton, setActiveSegButton] = useState(null); // 8 | 5 | null
  const [form] = Form.useForm();
  const [form2] = Form.useForm();
  const STORAGE_KEY = "lsc_values_v1";
  const STORAGE_KEY_SEG = "lsc_seg_values_v1";
  const loadSaved = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };
  const save = (obj) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch (e) {}
  };
  const loadSavedSeg = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_SEG);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };
  const saveSeg = (patch, options = {}) => {
    const { replace = false } = options;
    if (!patch || typeof patch !== "object") return;
    try {
      const current = replace ? {} : loadSavedSeg() || {};
      const payload = replace ? patch : { ...current, ...patch };
      localStorage.setItem(STORAGE_KEY_SEG, JSON.stringify(payload));
    } catch (e) {}
  };
  const formatCellNumber = (value, digits = 2) => {
    if (value === undefined || value === null) return "";
    const num = Number(value);
    if (Number.isNaN(num)) return "";
    return num.toFixed(digits);
  };
  const calculate = () => {
    checkPasswordBeforeAction(() => {
      const { d, h, h1, n1 } = form.getFieldsValue();
      const n = (h * n1) / h1; // N = H * N1 / H1
      const s = (Math.PI * d * n) / 60000; // S = π * D * N / 60000
      form.setFieldsValue({
        n: toHValue(n),
        s: toHValue(s),
      });
      // 同步到下方表单的只读 #109/#110，使用上方的测试频率 H1 和测试转速 N1
      syncTestFields();
      const v = form.getFieldsValue();
      save(v);
    });
  };
  const syncTestFields = () => {
    const { h1, n1 } = form.getFieldsValue();
    if (h1 !== undefined && h1 !== null && h1 !== "") {
      form2.setFieldValue("h109", h1);
    } else {
      form2.setFieldValue("h109", undefined);
    }
    if (n1 !== undefined && n1 !== null && n1 !== "") {
      form2.setFieldValue("n110", n1);
    } else {
      form2.setFieldValue("n110", undefined);
    }
    checkSegReady();
  };
  const checkSegReady = () => {
    const { s104, d106, d107, f108, h109, n110 } = form2.getFieldsValue();
    const ok = [s104, d106, d107, f108, h109, n110].every((v) => typeof v === "number" && v > 0);
    setSegReady(ok);
  };

  const buildSegments = (segCount = 8) => {
    const { s104, d106, d107, f108, h109, n110 } = form2.getFieldsValue();
    // guard
    if (!segCount || !s104 || !d106 || !d107 || !f108 || !h109 || !n110) {
      return { rows: [], diameters: [] };
    }

    // #104 = s104 (最佳线速度)
    // #106 = d106 (最大直径)
    // #107 = d107 (最小直径)
    // #108 = f108 (电机额定频率)
    // #109 = h109 (测试频率 H1)
    // #110 = n110 (测试转速 N1)

    if (segCount === 8) {
      // 8段计算逻辑
      // #140 = (#106 - #107) / 8
      const h140 = (d106 - d107) / 8;

      // 计算各段最大直径 #111-#118
      const h111 = d106;
      const h112 = d106 - h140 * 1;
      const h113 = d106 - h140 * 2;
      const h114 = d106 - h140 * 3;
      const h115 = d106 - h140 * 4;
      const h116 = d106 - h140 * 5;
      const h117 = d106 - h140 * 6;
      const h118 = d106 - h140 * 7;
      const h119 = d107;

      // #120 = #104 * 60000 / (#111 * 3.14159265)
      const h120 = (s104 * 60000) / (h111 * 3.14159265);

      // 计算各段最小线速度 #131-#138
      const h131 = (h112 * s104) / h111;
      const h132 = (h113 * s104) / h112;
      const h133 = (h114 * s104) / h113;
      const h134 = (h115 * s104) / h114;
      const h135 = (h116 * s104) / h115;
      const h136 = (h117 * s104) / h116;
      const h137 = (h118 * s104) / h117;
      const h138 = (h119 * s104) / h118;

      // #139 = (#131 + #132 + #133 + #134 + #135 + #136 + #137 + #138) / 8
      const h139 = (h131 + h132 + h133 + h134 + h135 + h136 + h137 + h138) / 8;

      // 重新计算各段最大直径 #121-#128
      const h121 = d106;
      const h122 = (h139 * h121) / s104;
      const h123 = (h139 * h122) / s104;
      const h124 = (h139 * h123) / s104;
      const h125 = (h139 * h124) / s104;
      const h126 = (h139 * h125) / s104;
      const h127 = (h139 * h126) / s104;
      const h128 = (h139 * h127) / s104;

      // 计算各段变频器运行频率 #141-#148
      const h141 = (h109 * h120) / n110;
      const h142 = (h121 * h141) / h122;
      const h143 = (h121 * h141) / h123;
      const h144 = (h121 * h141) / h124;
      const h145 = (h121 * h141) / h125;
      const h146 = (h121 * h141) / h126;
      const h147 = (h121 * h141) / h127;
      const h148 = (h121 * h141) / h128;

      // 计算各段变频器设置百分比 #151-#158
      const h151 = (h141 * 100) / f108;
      const h152 = (h142 * 100) / f108;
      const h153 = (h143 * 100) / f108;
      const h154 = (h144 * 100) / f108;
      const h155 = (h145 * 100) / f108;
      const h156 = (h146 * 100) / f108;
      const h157 = (h147 * 100) / f108;
      const h158 = (h148 * 100) / f108;

      const rows = [
        {
          key: 1,
          idx: "第1段",
          range: `${formatCellNumber(d106)}——${formatCellNumber(h122)}`,
          freq: formatCellNumber(h141),
          percent: formatCellNumber(h151),
        },
        {
          key: 2,
          idx: "第2段",
          range: `${formatCellNumber(h122)}——${formatCellNumber(h123)}`,
          freq: formatCellNumber(h142),
          percent: formatCellNumber(h152),
        },
        {
          key: 3,
          idx: "第3段",
          range: `${formatCellNumber(h123)}——${formatCellNumber(h124)}`,
          freq: formatCellNumber(h143),
          percent: formatCellNumber(h153),
        },
        {
          key: 4,
          idx: "第4段",
          range: `${formatCellNumber(h124)}——${formatCellNumber(h125)}`,
          freq: formatCellNumber(h144),
          percent: formatCellNumber(h154),
        },
        {
          key: 5,
          idx: "第5段",
          range: `${formatCellNumber(h125)}——${formatCellNumber(h126)}`,
          freq: formatCellNumber(h145),
          percent: formatCellNumber(h155),
        },
        {
          key: 6,
          idx: "第6段",
          range: `${formatCellNumber(h126)}——${formatCellNumber(h127)}`,
          freq: formatCellNumber(h146),
          percent: formatCellNumber(h156),
        },
        {
          key: 7,
          idx: "第7段",
          range: `${formatCellNumber(h127)}——${formatCellNumber(h128)}`,
          freq: formatCellNumber(h147),
          percent: formatCellNumber(h157),
        },
        {
          key: 8,
          idx: "第8段",
          range: `${formatCellNumber(h128)}——${formatCellNumber(d107)}`,
          freq: formatCellNumber(h148),
          percent: formatCellNumber(h158),
        }
      ];

      const diameters = [d106, h122, h123, h124, h125, h126, h127, h128, d107];
      return { rows, diameters };
    }

    if (segCount === 5) {
      // 5段计算逻辑
      // #140 = (#106 - #107) / 5
      const h140 = (d106 - d107) / 5;

      // 计算各段最大直径 #111-#116
      const h111 = d106;
      const h112 = d106 - h140 * 1;
      const h113 = d106 - h140 * 2;
      const h114 = d106 - h140 * 3;
      const h115 = d106 - h140 * 4;
      const h116 = d106 - h140 * 5;

      // #120 = #104 * 60000 / (#111 * 3.14159265)
      const h120 = (s104 * 60000) / (h111 * 3.14159265);

      // 计算各段最小线速度 #131-#135
      const h131 = (h112 * s104) / h111;
      const h132 = (h113 * s104) / h112;
      const h133 = (h114 * s104) / h113;
      const h134 = (h115 * s104) / h114;
      const h135 = (h116 * s104) / h115;

      // #139 = (#131 + #132 + #133 + #134 + #135) / 5
      const h139 = (h131 + h132 + h133 + h134 + h135) / 5;

      // 重新计算各段最大直径 #121-#125
      const h121 = d106;
      const h122 = (h139 * h121) / s104;
      const h123 = (h139 * h122) / s104;
      const h124 = (h139 * h123) / s104;
      const h125 = (h139 * h124) / s104;

      // 计算各段变频器运行频率 #141-#145
      const h141 = (h109 * h120) / n110;
      const h142 = (h121 * h141) / h122;
      const h143 = (h121 * h141) / h123;
      const h144 = (h121 * h141) / h124;
      const h145 = (h121 * h141) / h125;

      // 计算各段变频器设置百分比 #151-#155
      const h151 = (h141 * 100) / f108;
      const h152 = (h142 * 100) / f108;
      const h153 = (h143 * 100) / f108;
      const h154 = (h144 * 100) / f108;
      const h155 = (h145 * 100) / f108;

      const rows = [
        {
          key: 1,
          idx: "第1段",
          range: `${formatCellNumber(d106)}——${formatCellNumber(h122)}`,
          freq: formatCellNumber(h141),
          percent: formatCellNumber(h151),
        },
        {
          key: 2,
          idx: "第2段",
          range: `${formatCellNumber(h122)}——${formatCellNumber(h123)}`,
          freq: formatCellNumber(h142),
          percent: formatCellNumber(h152),
        },
        {
          key: 3,
          idx: "第3段",
          range: `${formatCellNumber(h123)}——${formatCellNumber(h124)}`,
          freq: formatCellNumber(h143),
          percent: formatCellNumber(h153),
        },
        {
          key: 4,
          idx: "第4段",
          range: `${formatCellNumber(h124)}——${formatCellNumber(h125)}`,
          freq: formatCellNumber(h144),
          percent: formatCellNumber(h154),
        },
        {
          key: 5,
          idx: "第5段",
          range: `${formatCellNumber(h125)}——${formatCellNumber(d107)}`,
          freq: formatCellNumber(h145),
          percent: formatCellNumber(h155),
        }
      ];

      const diameters = [d106, h122, h123, h124, h125, d107];
      return { rows, diameters };
    }

    return { rows: [], diameters: [] };
  };

  const handleSegCalc = (count) => {
    checkPasswordBeforeAction(() => {
      const { rows, diameters } = buildSegments(count);
      if (!rows.length) {
        setSegRows([]);
        setSegDiameters([]);
        setActiveSegButton(null);
        const values = form2.getFieldsValue();
        saveSeg({ ...values, segRows: [], activeSegButton: null, segDiameters: [] });
        return;
      }
      setSegRows(rows);
      setSegDiameters(diameters);
      setActiveSegButton(count);
      // persist segmented form values as well
      const values = form2.getFieldsValue();
      saveSeg({ ...values, activeSegButton: count, segRows: rows, segDiameters: diameters });
    });
  };
  const renderReadonlyCell = (val) => (
    <Input readOnly size="small" value={val ?? ""} style={{ textAlign: "left", width: "100%" }} />
  );
  const segColumns = [
    { title: "段次", dataIndex: "idx", width: 70 },
    {
      title: "直径区间 (mm)",
      dataIndex: "range",
      width: 200,
      render: renderReadonlyCell,
    },
    {
      title: "变频器运行频率 (Hz)",
      dataIndex: "freq",
      width: 150,
      align: "left",
      render: renderReadonlyCell,
    },
    {
      title: "变频器设置百分比 (%)",
      dataIndex: "percent",
      width: 150,
      align: "left",
      render: renderReadonlyCell,
    },
  ];
  const segTableWidth = segColumns.reduce((sum, column) => sum + (column.width || 0), 0);
  useEffect(() => {
    const saved = loadSaved();
    if (saved) {
      form.setFieldsValue(saved);
      setBtnState(check(saved, 4));
    }
    const savedSeg = loadSavedSeg();
    if (savedSeg) {
      const {
        activeSegButton: savedActiveButton,
        segRows: savedRows,
        segDiameters: savedDiameters,
        ...formValues
      } = savedSeg;
      form2.setFieldsValue(formValues);
      syncTestFields();
      checkSegReady();
      if (savedActiveButton) {
        setActiveSegButton(savedActiveButton);
      }
      if (Array.isArray(savedDiameters)) {
        setSegDiameters(savedDiameters);
      }
      if (Array.isArray(savedRows) && savedRows.length) {
        setSegRows(savedRows);
        if (
          savedActiveButton &&
          (!Array.isArray(savedDiameters) || !savedDiameters.length)
        ) {
          const { diameters: fallbackDiameters } = buildSegments(savedActiveButton);
          if (fallbackDiameters.length) {
            setSegDiameters(fallbackDiameters);
            saveSeg({ segDiameters: fallbackDiameters }, { replace: false });
          }
        }
      } else if (savedActiveButton) {
        const { rows: rebuiltRows, diameters: rebuiltDiameters } = buildSegments(savedActiveButton);
        if (rebuiltRows.length) {
          setSegRows(rebuiltRows);
          setSegDiameters(rebuiltDiameters);
          saveSeg({ segRows: rebuiltRows, segDiameters: rebuiltDiameters }, { replace: false });
        }
      }
    } else {
      syncTestFields();
      checkSegReady();
    }
  }, []);
  return (
    <div className="cpn-wrapper-horizontal">
      <Form
        style={{ maxWidth: 400, width: "90%" }}
        autoComplete="off"
        form={form}
        colon=":"
        onSubmit={calculate}
        layout={document.body.clientWidth <= 780 ? "vertical" : "horizontal"}
        onValuesChange={(_, values) => {
          setBtnState(check(values, 4));
          // sync test fields (#109/#110) from original H1/N1
          if ("h1" in values || "n1" in values) {
            syncTestFields();
          }
          // check seg section readiness
          checkSegReady();
          save(form.getFieldsValue());
        }}
      >
        <FormItem
          label="当前直径D"
          field="d"
          rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
        >
          <InputNumber placeholder="" size="large" />
        </FormItem>
        <FormItem
          label="当前频率H"
          field="h"
          rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
        >
          <InputNumber placeholder="" size="large" />
        </FormItem>
        <FormItem
          label="测试频率H1"
          field="h1"
          rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
        >
          <InputNumber placeholder="" size="large" />
        </FormItem>
        <FormItem
          label="测试转速N1"
          field="n1"
          rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
        >
          <InputNumber placeholder="" size="large" />
        </FormItem>
        <FormItem wrapperCol={{ offset: 5 }}>
          <Space>
            <Button type="primary" htmlType="submit" disabled={!btnState.calc}>
              立即计算
            </Button>
            <Button
              onClick={() => {
                form.resetFields();
                localStorage.removeItem(STORAGE_KEY);
                setBtnState({ calc: false, reset: false });
                // 清空下方与之联动的只读值
                form2.setFieldsValue({ h109: undefined, n110: undefined });
                checkSegReady();
              }}
              disabled={!btnState.reset}
            >
              重置
            </Button>
          </Space>
        </FormItem>
        <FormItem label="当前转速N" field="n">
          <Input
            id="result_lsc_n"
            size="large"
            readOnly
            onClick={copy}
            placeholder="计算结果在此次显示"
            disabled={!btnState.calc}
            suffix={<IconCopy style={{ cursor: "pointer" }} onClick={copy.bind(null, null, "#result_lsc_n")} />}
          />
        </FormItem>
        <FormItem label="当前线速度S" field="s">
          <Input
            id="result_lsc_s"
            size="large"
            readOnly
            onClick={copy}
            placeholder="计算结果在此次显示"
            disabled={!btnState.calc}
            suffix={<IconCopy style={{ cursor: "pointer" }} onClick={copy.bind(null, null, "#result_lsc_s")} />}
          />
        </FormItem>
      </Form>

      {/* 分割线 */}
      <div style={{ width: "100%", borderTop: "1px dashed #d9d9d9", margin: "16px 0" }} />

      {/* 砂轮恒线速度直径分段及变频器设置计算 */}
      <div style={{ width: "100%", fontWeight: 600, marginBottom: 16, textAlign: "center" }}>
        砂轮恒线速度直径分段及变频器设置计算
      </div>
      <Form
        style={{ width: "100%", maxWidth: 700 }}
        autoComplete="off"
        form={form2}
        colon=":"
        layout={document.body.clientWidth <= 780 ? "vertical" : "horizontal"}
        onValuesChange={(_, allValues) => {
          checkSegReady();
          // 保存所有表单值和当前激活的分段按钮状态
          saveSeg({ ...allValues, activeSegButton, segRows, segDiameters });
        }}
      >
        <div className="seg-wide-labels">
          <FormItem
            label="最佳线速度S(#104)"
            field="s104"
            labelCol={{ span: 9 }}
            wrapperCol={{ span: 15 }}
            rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
          >
            <InputNumber placeholder="例如 35 (m/s)" size="large" />
          </FormItem>
          <FormItem
            label="最大直径Dmax(#106)"
            field="d106"
            labelCol={{ span: 9 }}
            wrapperCol={{ span: 15 }}
            rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
          >
            <InputNumber placeholder="" size="large" />
          </FormItem>
          <FormItem
            label="最小直径Dmin(#107)"
            field="d107"
            labelCol={{ span: 9 }}
            wrapperCol={{ span: 15 }}
            rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
          >
            <InputNumber placeholder="" size="large" />
          </FormItem>
          <FormItem
            label="电机额定频率Frated(#108)"
            field="f108"
            labelCol={{ span: 9 }}
            wrapperCol={{ span: 15 }}
            rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
          >
            <InputNumber placeholder="例如 50 或 60" size="large" />
          </FormItem>
          <FormItem
            label="测试频率H1(#109)"
            field="h109"
            labelCol={{ span: 9 }}
            wrapperCol={{ span: 15 }}
            rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("需先在上方填写‘测试频率 H1’!")) }]}
          >
            <InputNumber placeholder="随上方H1同步" size="large"  />
          </FormItem>
          <FormItem
            label="测试转速N1(#110)"
            field="n110"
            labelCol={{ span: 9 }}
            wrapperCol={{ span: 15 }}
            rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("需先在上方填写‘测试转速 N1’!")) }]}
          >
            <InputNumber placeholder="随上方N1同步" size="large"  />
          </FormItem>
        </div>
        <FormItem wrapperCol={{ offset: 9 }}>
          <Space>
            <Button
              type={activeSegButton === 8 ? "primary" : "default"}
              onClick={() => handleSegCalc(8)}
              disabled={!segReady}
            >
              分 8 段计算
            </Button>
            <Button
              type={activeSegButton === 5 ? "primary" : "default"}
              onClick={() => handleSegCalc(5)}
              disabled={!segReady}
            >
              分 5 段计算
            </Button>
          </Space>
        </FormItem>
        {segRows.length > 0 && (
          <div style={{ width: "100%", overflowX: "auto" }}>
            <Table
              pagination={false}
              size="small"
              scroll={segTableWidth ? { x: segTableWidth } : undefined}
              columns={segColumns}
              data={segRows}
            />
          </div>
        )}
      </Form>
    </div>
  );
};
export default LinearSpeedCalculation;
